/**
 * Bare-bones service for handling AI streaming functionality
 */
export default class StreamingService {
  /**
   * @param {Object} scribeApi - The API service to use for requests
   * @param {boolean} debugMode - Whether to log debug information
   */
  constructor(scribeApi, debugMode = false) {
    this.scribeApi = scribeApi;
    this.debugMode = debugMode;
    this.abortController = null;
  }
  
  /**
   * Start streaming data with the provided payload
   * 
   * @param {Object} payload - Data to send to the API
   * @param {Object} callbacks - Callback functions for streaming lifecycle events
   * @returns {Promise<boolean>} Promise that resolves when streaming is complete
   */
  async startStreaming(payload, callbacks = {}) {
    const {
      onStart = () => {},
      onChunk = () => {},
      onInterface = () => {},
      onSystem = () => {},
      onContent = () => {},
      onFinish = () => {},
      onError = () => {}
    } = callbacks;
    
    try {
      // Create a new AbortController for this stream
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();
      
      // Call the start callback
      onStart();
      
      // Process the stream
      await this.scribeApi.processAICommandStream(
        payload,
        (chunk) => this.processChunk(chunk, { onChunk, onInterface, onSystem, onContent })
      );
      
      // Call the finish callback
      onFinish();
      
      return true;
    } catch (error) {
      // Handle aborted requests differently
      if (error instanceof Error && error.name === 'AbortError') {
        if (this.debugMode) console.log('Stream request was aborted');
        return false;
      }
      
      if (this.debugMode) console.error('Error in streaming request:', error);
      onError(error instanceof Error ? error.message : 'Unknown error occurred');
      throw error;
    }
  }
  
  /**
   * Stop the current streaming operation
   */
  stopStreaming() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }
  
  /**
   * Process an individual chunk from the stream
   * @private
   */
  processChunk(chunk, callbacks) {
    const { onChunk, onInterface, onSystem, onContent } = callbacks;
    
    if (!chunk) return;
    
    // Pass the raw chunk data to the callback
    onChunk(chunk);
    
    try {
      // Process SSE format (data: {...})
      if (typeof chunk === 'string') {
        const lines = chunk.split('\n').filter(line => line.trim() !== '');
        
        for (const line of lines) {
          let jsonData = null;
          
          // Extract JSON from SSE format
          if (line.startsWith('data: ')) {
            try {
              const jsonStr = line.substring(6).trim();
              jsonData = JSON.parse(jsonStr);
            } catch (e) {
              if (this.debugMode) console.error("Error parsing SSE JSON:", e);
              continue;
            }
          } else {
            // Try to parse as direct JSON
            try {
              jsonData = JSON.parse(line);
            } catch (e) {
              // Not JSON, could be plain text
              if (!line.includes('{') && !line.includes('}')) {
                onContent(line);
              }
              continue;
            }
          }
          
          if (!jsonData) continue;
          
          // Process different types of messages
          if (jsonData.interface) {
            onInterface(jsonData.interface);
          } else if (jsonData.system) {
            onSystem(jsonData.system);
          } else if (jsonData.message) {
            onContent(jsonData.message);
          } else if (jsonData.content) {
            onContent(jsonData.content);
          }
        }
      }
    } catch (error) {
      if (this.debugMode) console.error('Error processing chunk:', error);
    }
  }
}